import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Badge, Button, Card, Group, Skeleton, Table, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar, IconCircleCheck, IconFilePencil } from '@tabler/icons';
import { DateTime } from 'luxon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { GET_ADMIN_USER, GET_ADMIN_USER_STATS } from '../../../graphql/queries';
import { Error } from '../../../Components/Error';
import { CountryFlag } from '../../../Components/CountryFlag';
import { EditUserPlanModal } from '../../../Components/Admin/EditUserPlanModal';
import { useStyles } from '../../../styles';

const DATE_FORMAT = 'yyyy/LL/dd';
const URL_DATE_FORMAT = 'yyyy-LL-dd';
const DEFAULT_RANGE_DAYS = 30;
// admin_get_user_stats rejects ranges wider than 365 days
const MAX_RANGE_DAYS = 364;

type DateRangeValue = [Date | null, Date | null];

export function AdminUserViewPage() {
  const { userId } = useParams();
  const { classes } = useStyles();
  const [params, setParams] = useSearchParams();

  const today = DateTime.now().startOf('day');

  const parseDateParam = (key: string, fallback: DateTime) => {
    const raw = params.get(key);
    const parsed = raw ? DateTime.fromFormat(raw, URL_DATE_FORMAT) : null;
    return parsed && parsed.isValid ? parsed : fallback;
  };

  const rangeBegin = parseDateParam('from', today.minus({ days: DEFAULT_RANGE_DAYS }));
  const rangeEnd = parseDateParam('to', today);

  const [pickerValue, setPickerValue] = useState<DateRangeValue>([
    rangeBegin.toJSDate(),
    rangeEnd.toJSDate(),
  ]);
  const [isEditing, setIsEditing] = useState(false);

  const rangeBeginIso = rangeBegin.toISODate();
  const rangeEndIso = rangeEnd.toISODate();

  useEffect(() => {
    // keep the picker in sync when the range changes via URL (back/forward)
    setPickerValue([rangeBegin.toJSDate(), rangeEnd.toJSDate()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeBeginIso, rangeEndIso]);

  const onRangeChange = (value: DateRangeValue) => {
    setPickerValue(value);
    const [from, to] = value;
    if (!from || !to) {
      return;
    }
    setParams({
      from: DateTime.fromJSDate(from).toFormat(URL_DATE_FORMAT),
      to: DateTime.fromJSDate(to).toFormat(URL_DATE_FORMAT),
    });
  };

  const now = DateTime.now().set({ minute: 0, second: 0, millisecond: 0 });
  const dailyBegin = rangeBegin.startOf('day');
  const rawEnd = rangeEnd.endOf('day');
  const statsEnd = rawEnd > now ? now : rawEnd;
  const hourlyBegin = statsEnd.minus({ hours: 24 });

  const formatter = Intl.NumberFormat('en', { notation: 'compact' });
  const dateFormatter = (date: number) => DateTime.fromMillis(date).toFormat('D HH:mm');
  const dayFormatter = (date: number) => DateTime.fromMillis(date).toFormat('D');
  const valueFormatter = (value: number) => formatter.format(value);

  const { loading, error, data } = useQuery(GET_ADMIN_USER, {
    variables: { userId },
  });

  const { data: statsData } = useQuery(GET_ADMIN_USER_STATS, {
    variables: {
      userId,
      begin: hourlyBegin.toISO(),
      end: statsEnd.toISO(),
      begin2: dailyBegin.toISO(),
    },
  });

  if (loading) {
    return (
      <>
        <Skeleton height={8} mt={6} radius="xl" />
        <Skeleton height={200} mt={6} radius="md" />
        <Skeleton height={200} mt={6} radius="md" />
      </>
    );
  }

  if (error) return <Error text={error.toString()} />;

  const user = data.adminUser;
  if (!user) return <Text>User not found.</Text>;

  const projects = data.adminUserProjects ?? [];
  const plans = (data.adminPlans ?? []).map((plan: any) => plan.slug);

  const hourlyData = statsData?.adminGetUserStats?.map((row: any) => ({
    name: DateTime.fromISO(row.date).toMillis(),
    count: row.count,
  })) ?? [];

  const dailyData = statsData?.dayStats?.map((row: any) => ({
    name: DateTime.fromISO(row.date).toMillis(),
    count: row.count,
  })) ?? [];

  return (
    <>
      <EditUserPlanModal
        user={isEditing ? user : null}
        plans={plans}
        refetchQueries={[{ query: GET_ADMIN_USER, variables: { userId } }]}
        onClose={() => setIsEditing(false)}
      />

      <Group position="apart" align="flex-start" mb="md">
        <div>
          <Title order={2}>{user.email}</Title>
          <Text color="dimmed" size="sm">{user.id}</Text>
        </div>
        <Group align="flex-end">
          <DatePickerInput
            type="range"
            icon={<IconCalendar size="1rem" />}
            value={pickerValue}
            onChange={onRangeChange}
            minDate={today.minus({ days: MAX_RANGE_DAYS }).toJSDate()}
            maxDate={today.toJSDate()}
            allowSingleDateInRange
          />
          <Button
            leftIcon={<IconFilePencil size="1rem" />}
            color="green"
            variant="light"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        </Group>
      </Group>

      <Card withBorder mb={20}>
        <Table>
          <tbody>
            <tr>
              <td><Text weight={500}>Active / Verified / Admin</Text></td>
              <td>
                {user.active ? <IconCircleCheck /> : '—'}
                {user.verified ? <IconCircleCheck /> : '—'}
                {user.isAdmin ? <IconCircleCheck /> : '—'}
              </td>
            </tr>
            <tr>
              <td><Text weight={500}>Plan</Text></td>
              <td>
                {user.data?.planSlug || '—'}
                {user.data?.planEndDate && (
                  <Text size="xs" color="dimmed">Expires: {DateTime.fromISO(user.data.planEndDate).toFormat(DATE_FORMAT)}</Text>
                )}
              </td>
            </tr>
            <tr>
              <td><Text weight={500}>Billing start</Text></td>
              <td>{user.data?.billingBeginDate ? DateTime.fromISO(user.data.billingBeginDate).toFormat(DATE_FORMAT) : '—'}</td>
            </tr>
            <tr>
              <td><Text weight={500}>Blocked till</Text></td>
              <td>{user.data?.blockedTill ? DateTime.fromISO(user.data.blockedTill).toFormat(DATE_FORMAT) : '—'}</td>
            </tr>
            <tr>
              <td><Text weight={500}>Telegram</Text></td>
              <td>{user.data?.telegramUsername || '—'}</td>
            </tr>
            <tr>
              <td><Text weight={500}>Created</Text></td>
              <td>{DateTime.fromISO(user.insertInstant).toFormat(DATE_FORMAT)}</td>
            </tr>
            {user.currentStats && (
              <tr>
                <td><Text weight={500}>Current stats</Text></td>
                <td>
                  {user.currentStats.total.toLocaleString()} reqs
                  {' '}
                  <Badge color={user.currentStats.isActive ? 'green' : 'gray'}>
                    {user.currentStats.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  {user.currentStats.isBlocked && <Badge color="red" ml={4}>Blocked</Badge>}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Card mb={20}>
        <Text fz="xl" mb={30} className={classes.label}>
          Requests per hour — 24h to {statsEnd.toFormat(DATE_FORMAT + ' HH:mm')}
        </Text>
        <ResponsiveContainer height={200}>
          <LineChart data={hourlyData}>
            <CartesianGrid strokeDasharray="1 5" />
            <XAxis dataKey="name" tickFormatter={dateFormatter} type="number" domain={[hourlyBegin.toMillis(), statsEnd.toMillis()]} />
            <YAxis tickFormatter={valueFormatter} />
            <Tooltip labelFormatter={dateFormatter} />
            <Line type="monotone" dataKey="count" stroke="#82ca9d" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card mb={20}>
        <Text fz="xl" mb={30} className={classes.label}>
          Requests per day — {rangeBegin.toFormat(DATE_FORMAT)} to {rangeEnd.toFormat(DATE_FORMAT)}
        </Text>
        <ResponsiveContainer height={200}>
          <LineChart data={dailyData}>
            <CartesianGrid strokeDasharray="1 5" />
            <XAxis dataKey="name" tickFormatter={dayFormatter} type="number" domain={[dailyBegin.toMillis(), statsEnd.toMillis()]} />
            <YAxis tickFormatter={valueFormatter} />
            <Tooltip labelFormatter={dayFormatter} />
            <Line type="monotone" dataKey="count" stroke="#82ca9d" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Title order={3} mb="sm">Projects ({projects.length})</Title>
      <Card withBorder>
        {projects.length === 0 ? (
          <Text color="dimmed">No projects.</Text>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Network</th>
                <th>Plan</th>
                <th>Usage</th>
                <th>Last IP</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p: any) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.network?.title || p.network?.slug}</td>
                  <td>
                    {p.plan?.slug}
                    {p.plan && (
                      <Text size="xs" color="dimmed">{p.plan.requestsPerMonth.toLocaleString()} req/mo</Text>
                    )}
                  </td>
                  <td>{p.currentStats?.total?.toLocaleString() ?? '—'}</td>
                  <td>
                    {p.latestUsedIp ? (
                      <>
                        <CountryFlag code={p.latestUsedIpCountry} /> {p.latestUsedIp}
                      </>
                    ) : '—'}
                  </td>
                  <td><Badge>{p.status}</Badge></td>
                  <td>{DateTime.fromISO(p.createdAt).toFormat(DATE_FORMAT)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
